import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from "@/hooks/use-toast";
import { Loader2 } from "lucide-react";

export default function AuthCallbackPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [searchParams] = useSearchParams();
  const [isProcessing, setIsProcessing] = useState(true);
  const [retryCount, setRetryCount] = useState(0);
  const [processingLog, setProcessingLog] = useState<string[]>([]);

  const addToLog = (message: string) => {
    console.log(`[Auth Callback] ${message}`);
    setProcessingLog(prev => [...prev, message]);
  };

  useEffect(() => {
    const handleAuthCallback = async () => {
      try {
        const isRecovery = searchParams.get('type') === 'recovery';
        const destination = searchParams.get('destination') || (isRecovery ? '' : '');
        
        addToLog(`Processing auth callback: isRecovery=${isRecovery}, destination=${destination}, timestamp=${new Date().toISOString()}`);
        
        // Give Supabase more time to process the auth token
        addToLog('Waiting for Supabase to process auth token... (first delay: 2000ms)');
        await new Promise(resolve => setTimeout(resolve, 2000));
        
        const { data, error } = await supabase.auth.getSession();
        
        if (error) {
          addToLog(`Auth callback error: ${error.message}`);
          toast({
            title: "Authenticatie fout",
            description: "Er is een fout opgetreden bij het verwerken van de authenticatie.",
            variant: "destructive",
          });
          navigate('/auth/login');
          return;
        }

        if (data.session) {
          addToLog(`Session found in first attempt: user_id=${data.session.user?.id}`);
          
          if (isRecovery) {
            addToLog(`Recovery flow detected, redirecting to destination: ${destination}`);
            toast({
              title: "Welkom terug",
              description: "Je wachtwoord is succesvol gereset en je bent nu ingelogd.",
            });
            
            // For recovery flow, ensure we wait a bit longer to ensure the session is properly registered
            await new Promise(resolve => setTimeout(resolve, 500));
            
            // Redirect to homepage or specified destination
            navigate(destination ? `/${destination}` : '/', { replace: true });
          } else {
            addToLog('Regular auth flow, redirecting to homepage');
            toast({
              title: "Succesvol ingelogd",
              description: "Je bent nu ingelogd!",
            });
            navigate('/', { replace: true });
          }
        } else {
          addToLog('No session found, retrying in 3 seconds...');
          
          // Try again with a longer delay
          setTimeout(async () => {
            try {
              const { data: retryData, error: retryError } = await supabase.auth.getSession();
              
              if (retryError) {
                addToLog(`Session retry error: ${retryError.message}`);
                navigate('/auth/login');
                return;
              }
              
              if (retryData.session) {
                addToLog(`Session found on retry: user_id=${retryData.session.user?.id}`);
                
                if (isRecovery) {
                  addToLog(`Recovery flow detected in retry, redirecting to destination: ${destination}`);
                  toast({
                    title: "Welkom terug",
                    description: "Je wachtwoord is succesvol gereset en je bent nu ingelogd.",
                  });
                  
                  // For recovery flow, ensure we wait a bit longer to ensure the session is properly registered
                  await new Promise(resolve => setTimeout(resolve, 500));
                  
                  navigate(destination ? `/${destination}` : '/', { replace: true });
                } else {
                  navigate('/', { replace: true });
                }
              } else {
                addToLog('Still no session after retry, checking one last time...');
                
                // Last attempt with an even longer delay
                setRetryCount(prev => prev + 1);
                
                setTimeout(async () => {
                  try {
                    const { data: finalData } = await supabase.auth.getSession();
                    
                    if (finalData.session) {
                      addToLog(`Session found on final attempt: user_id=${finalData.session.user?.id}`);
                      
                      if (isRecovery) {
                        // For recovery flow, ensure we wait a bit longer to ensure the session is properly registered
                        await new Promise(resolve => setTimeout(resolve, 500));
                        
                        navigate(destination ? `/${destination}` : '/', { replace: true });
                      } else {
                        navigate('/', { replace: true });
                      }
                    } else {
                      addToLog('No session found after multiple attempts');
                      navigate('/auth/login');
                    }
                  } catch (finalError) {
                    addToLog(`Final retry error: ${finalError}`);
                    navigate('/auth/login');
                  } finally {
                    setIsProcessing(false);
                  }
                }, 3000);
              }
            } catch (retryAttemptError) {
              addToLog(`Retry attempt error: ${retryAttemptError}`);
              navigate('/auth/login');
              setIsProcessing(false);
            }
          }, 3000);
        }
      } catch (error) {
        addToLog(`Auth callback error: ${error}`);
        navigate('/auth/login');
        setIsProcessing(false);
      }
    };

    handleAuthCallback();
  }, [navigate, toast, searchParams]);

  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center max-w-md w-full px-4">
        <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" />
        <h2 className="text-2xl font-semibold mb-2">Bezig met inloggen...</h2>
        <p className="text-muted-foreground">Een moment geduld alsjeblieft.</p>
        {retryCount > 0 && (
          <p className="text-sm text-muted-foreground mt-4">
            Sessie initialiseren, poging {retryCount+1}/3...
          </p>
        )}
        
        {/* Show processing log in development mode */}
        {process.env.NODE_ENV === 'development' && (
          <div className="mt-6 text-left bg-gray-50 dark:bg-gray-800 p-4 rounded-md text-xs overflow-auto max-h-64">
            <h3 className="font-medium mb-2">Debug Log:</h3>
            {processingLog.map((log, i) => (
              <div key={i} className="mb-1">{log}</div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
